import { IResourceConfig } from 'library/ts/player/adapter/interfaces/resource/config/IResourceConfig';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';
import { FreewheelModel } from 'tag/ts/tag/model/ads/freewheel/FreewheelModel';
import { FreewheelConstants } from 'tag/ts/tag/model/FreewheelConstants';

/**
 * @copyright © Paramount Streaming
 * @description Utility class for Freewheel. Contains functionality needed for Freewheel requests separate from data.
 */
export class FWUtilities {

	public static _name: string = 'FWUtilities';
	private static _instance: FWUtilities = new FWUtilities();
	#logger: ILogger;

	/**
	 * @description Values which should not be encoded
	 */
	readonly #nonEncodingValues = {
		[ FreewheelConstants.getInstance().randomTag ]: FreewheelConstants.getInstance().randomTag
	};

	constructor() {
		if ( FWUtilities._instance ) {
			throw new Error( FWUtilities._name + ' is a Singleton and can only be accessed through ' + FWUtilities._name + '.getInstance()' );
		}
		FWUtilities._instance = this;
		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', 'Tag:', FWUtilities._name ] } );
	}

	/**
	 * @description Returns reference to Utilities class instance
	 * @return {Utilities}
	 */
	get utils(): Utilities {
		return window?.SmartTag?.utilities;
	}

	public static getInstance(): FWUtilities {
		return FWUtilities._instance;
	}

	/**
	 * @description Encode input to pass a a URL param.
	 * @param {string} input
	 * @return {string}
	 */
	transformToUrlValue = ( input: string ): string => {
		if (
			this.utils.Type.isNullOrUndefined( input ) ||
			( !this.utils.Type.isString( input ) && !this.utils.Type.isNumber( input ) && !this.utils.Type.isBoolean( input ) ) ) return input;

		// certain values should not be encoded
		if ( !this.utils.Type.isNullOrUndefined( this.#nonEncodingValues[ input ] ) ) return this.#nonEncodingValues[ input ];

		return encodeURIComponent( input.toString() );
	};

	/**
	 * @description Create and return a valid URL query string from FreeWheelModel
	 * @param {FreewheelModel} fwModel
	 * @param {string} url Append parameters to existing url string. If unset, will create and return a new url string.
	 * @return {string}
	 */
	createFWUrl = ( fwModel: FreewheelModel, url = '' ): string => {
		if ( this.utils.Type.isNullOrUndefined( fwModel ) ) {
			this.#logger.error( 'createFWUrl', 'missing/invalid FreeWheelModel:', fwModel );
			return url;
		}

		const params = fwModel.adParameters;
		const utils = window.SmartTag.utilities;
		Object.keys( params )
			.filter( ( key ) => !utils.Type.isEmpty( params[ key ] ) )
			.forEach( ( key ) => {
				if ( utils.Type.isArray( params[ key ] ) && params[ key ].length > 0 ) {
					params[ key ]
						.filter( ( item ) => !utils.Type.isEmpty( item ) )
						.forEach( ( item ) => url = utils.URL.replaceParam( key, this.transformToUrlValue( item ), url ) );
				}

				if (
					this.utils.Type.isString( params[ key ] ) ||
					this.utils.Type.isNumber( params[ key ] ) ||
					this.utils.Type.isBoolean( params[ key ] )
				) {
					url = utils.URL.replaceParam( key, this.transformToUrlValue( params[ key ] ), url );
				}
			} );
		return url;
	};

	/**
	 * @description Append FreeWheel parameters to an existing object
	 * @param {IResourceConfig} resource
	 * @param {FreewheelModel} model
	 * @return {IResourceConfig}
	 */
	mergeFWParamsWithResourceConfig = ( resource: IResourceConfig, model: FreewheelModel ): IResourceConfig => {
		if ( this.utils.Type.isNullOrUndefined( resource ) ) {
			this.#logger.warn( 'mergeFWParamsWithResourceConfig', 'resource unset', 'resource:', resource );
			return resource;
		}

		if ( this.utils.Type.isNullOrUndefined( model ) ) {
			this.#logger.error( 'mergeFWParamsWithResourceConfig', 'FreewheelModel unset', 'model:', model );
			return resource;
		}

		if ( this.utils.Type.isNullOrUndefined( resource.ad?.ssai?.adParameters ) ) {
			// init adParameters if not set
			resource.ad.ssai.adParameters = {};
		}

		const params = model.adParameters;
		const utils = window.SmartTag.utilities;

		Object.keys( params )
			.filter( ( key ) => !utils.Type.isEmpty( params[ key ] ) )
			.forEach( ( key ) => {
				if ( key === 'bundle' || key === 'imafw_bundle' ) {
					// bundle values must be encoded and passed within cust_params to be read properly by MOPS
					// https://paramountplus.atlassian.net/browse/CBSVIDEO-6134
					const bundleValue: string = params[ key ]
						.filter( ( item ) => !utils.Type.isEmpty( item ) )
						.map( ( item ) => item )
						.join( ',' );
					resource.ad.ssai.adParameters.cust_params = `bundle=${bundleValue}`;

				} else if (
					this.utils.Type.isString( params[ key ] ) ||
					this.utils.Type.isNumber( params[ key ] ) ||
					this.utils.Type.isBoolean( params[ key ] )
				) {
					resource.ad.ssai.adParameters[ key ] = params[ key ];
				}
			} );

		// INFO: DAI SDK does encoding for us so we should not do any url encoding ourselves
		this.#logger.log( 'mergeFWParamsWithResourceConfig', 'returning resource:', resource );
		return resource;
	};
}